import React, { Dispatch, FC, useEffect, useState } from "react"

import { ReactComponent as IconOffer } from "@common_assets/svg/offer-outlined.svg"
import { ReactComponent as IconQuestionMark } from "@common_assets/svg/question.svg"
import { yupResolver } from "@hookform/resolvers/yup"
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material"
import { IPostEditClass } from "api/reactQuery/mutations/classes.type"
import { useQueryAttendanceSettings } from "api/reactQuery/queries/teacherAdmin"
import { AttendanceLevel } from "api/reactQuery/queries/teacherAdmin.types"
import Dialog from "components/common/dialog/dialog"
import DynamicIcon from "components/common/icon/dynamicIcon"
import { icons } from "components/common/icon/dynamicIcon/DynamicIcon.config"
import { HREFLINK } from "components/common/link/hreflink"
import { useDialog } from "hooks/dialog"
import { useCustomPayment } from "hooks/payment"
import { Action } from "layout/asideNav/navigation/asideOptions/classAside/ClassAside.utility"
import { useForm } from "react-hook-form"
import { Trans, useTranslation } from "react-i18next"
import { UserSchoolPlan } from "ts/enums/User"
import { IClassShort } from "ts/interfaces/Class"
import { IUserMe } from "ts/interfaces/User"
import { payCheck_interval } from "utils/paycheckInterval"

import {
  clearError,
  defaultValues,
  schemaStructure,
  setterValue,
} from "./AddNewClass.config"
import {
  ErrorStyle,
  TabsStyle,
  TabStyle,
  ToggleButtonStyled,
} from "./AddNewClass.style"
import {
  AddNewClassTab,
  IResponseNewClass,
  ISaveCallbacks,
} from "./AddNewClass.types"
import Advance from "./advance"
import ClassAttendanceForm from "./classAttendanceForm/ClassAttendanceForm"
import UnsavedChangesDialog from "./unsavedChangesDialog"
import {
  grades,
  hasUnsavedAttendanceChanges,
  hasUnsavedGeneralChanges,
} from "./utily"

interface IProps {
  state: IResponseNewClass
  setState: Dispatch<React.SetStateAction<IResponseNewClass>>
  id?: string
  createNewClass?: (data: IResponseNewClass) => void
  editClass?: (data: IPostEditClass, callbacks?: ISaveCallbacks) => void
  saveMath?: (callbacks?: ISaveCallbacks) => void
  dispatchState?: Dispatch<Action>
  getDataId?: IResponseNewClass
  allClasses?: IClassShort[]
  isEditClass?: boolean
  userInfo: IUserMe | null
}

const AddNewClass: FC<IProps> = ({
  state,
  editClass,
  saveMath,
  setState,
  id,
  createNewClass,
  dispatchState,
  getDataId,
  allClasses,
  isEditClass,
  userInfo,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const [isApplySettings, setIsApplySettings] = useState(false)
  const [copyClassId, setCopyClassId] = useState<number | undefined>(undefined)
  const [tab, setTab] = useState(AddNewClassTab.General)

  const [attendanceFormValid, setAttendanceFormValid] = useState(true)
  const [showAttendanceErrors, setShowAttendanceErrors] = useState(false)
  const [isAdvancedDirty, setIsAdvancedDirty] = useState(false)
  const [pendingTab, setPendingTab] = useState<AddNewClassTab | null>(null)
  const [isSavingBeforeSwitch, setIsSavingBeforeSwitch] = useState(false)

  const { data: attendanceSettings } = useQueryAttendanceSettings({
    options: { enabled: !!isEditClass },
  })
  const showAttendanceTab =
    !!isEditClass &&
    attendanceSettings?.data.has_attendance_integration &&
    attendanceSettings?.data.attendance_level === AttendanceLevel.Section

  const getIsActiveTabDirty = () => {
    if (!isEditClass) return false
    if (tab === AddNewClassTab.Advanced) return isAdvancedDirty
    if (!getDataId) return false
    if (tab === AddNewClassTab.Attendance) {
      return hasUnsavedAttendanceChanges(state, getDataId)
    }
    return hasUnsavedGeneralChanges(state, getDataId)
  }

  const handleChange = (_event: React.SyntheticEvent, newVal: AddNewClassTab) => {
    if (getIsActiveTabDirty()) {
      setPendingTab(newVal)
      return
    }
    setTab(newVal)
  }

  const methods = useForm<IResponseNewClass>({
    resolver: yupResolver(schemaStructure),
    defaultValues: defaultValues,
  })

  const {
    isOpen: isDialogOpenUpgradePlan,
    handleOpen: handleOpenDialogUpgradePlan,
    handleClose: handleCloseDialogUpgradePlan,
  } = useDialog()

  const {
    isOpen: isDialogOpenSavingsOff,
    handleOpen: handleOpenDialogSavingsOff,
    handleClose: handleCloseDialogSavingsOff,
  } = useDialog()

  const { payIt, isLoadingPayments } = useCustomPayment()

  useEffect(() => {
    clearError(state, methods)
  }, [state])

  useEffect(() => {
    setterValue(state, methods, isApplySettings)
  }, [state, isApplySettings])

  // Edit mode saves only the active tab
  const saveActiveTab = (callbacks?: ISaveCallbacks) => {
    if (tab === AddNewClassTab.Advanced) {
      saveMath && saveMath(callbacks)
      return
    }

    if (tab === AddNewClassTab.Attendance) {
      if (!attendanceFormValid) {
        setShowAttendanceErrors(true)
        callbacks?.onSettled?.()
        return
      }
      setShowAttendanceErrors(false)
      editClass &&
        editClass(
          {
            classId: Number(state.id),
            data: { attendance_bonus_amount: state.attendance_bonus_amount ?? null },
          },
          callbacks,
        )
      return
    }

    const { name, attendance_bonus_amount: _attendanceBonusAmount, ...rest } =
      state
    editClass &&
      editClass(
        {
          classId: Number(state.id),
          data: {
            ...rest,
            ...(getDataId?.name === name ? {} : { name }),
            description: "",
            grade: state.grade,
            icon: state.icon,
            paycheck_interval: state.paycheck_interval,
            paycheck_automatic: state.paycheck_automatic,
            expense_automatic: state.expense_automatic,
            expense_interval: state.expense_interval,
            savings_account_option: state.savings_account_option,
            interest_option: state.interest_option,
            interest_granting_interval: state.interest_granting_interval,
            saving_interest_rate: state.interest_option
              ? Number(state.saving_interest_rate)
              : 0,
            round_interest_to_whole_number:
              state.round_interest_to_whole_number,
          },
        },
        callbacks,
      )
  }

  const closeUnsavedChangesDialog = () => {
    setPendingTab(null)
    setIsSavingBeforeSwitch(false)
  }

  const saveAndSwitchTab = () => {
    if (!pendingTab) return
    setIsSavingBeforeSwitch(true)
    const callbacks: ISaveCallbacks = {
      onSuccess: () => {
        setTab(pendingTab)
        closeUnsavedChangesDialog()
      },
      onSettled: closeUnsavedChangesDialog,
    }
    // Only the General tab is validated by the form schema
    if (tab !== AddNewClassTab.General) {
      saveActiveTab(callbacks)
      return
    }
    methods.handleSubmit(
      () => saveActiveTab(callbacks),
      closeUnsavedChangesDialog,
    )()
  }

  const discardAndSwitchTab = () => {
    if (!pendingTab) return
    if (tab !== AddNewClassTab.Advanced && getDataId) {
      setState(getDataId)
    }
    setShowAttendanceErrors(false)
    setTab(pendingTab)
    closeUnsavedChangesDialog()
  }

  const handleClick = (data: IResponseNewClass) => {
    if (id === "form") {
      if (createNewClass) {
        createNewClass({
          ...data,
          expense_interval:
            data?.expense_interval === "" ? null : data?.expense_interval,
          paycheck_interval:
            data?.paycheck_interval === "" ? null : data?.paycheck_interval,
          interest_granting_interval:
            data?.interest_granting_interval === ""
              ? null
              : data?.interest_granting_interval,
          saving_interest_rate: data.interest_option
            ? Number(data?.saving_interest_rate)
            : 0,
          copy_settings: isApplySettings,
          copy_from_id: copyClassId,
        })
      }
    } else {
      saveActiveTab()
    }
  }

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    if (tab !== AddNewClassTab.General) {
      event.preventDefault()
      saveActiveTab()
      return
    }
    methods.handleSubmit(handleClick)(event)
  }

  return (
    <Box
      component="form"
      id={id}
      onSubmit={handleFormSubmit}
      // In Advanced, let the students list shrink to the space left in the dialog
      sx={
        tab === AddNewClassTab.Advanced
          ? { display: "flex", flexDirection: "column", minHeight: 0 }
          : undefined
      }
    >
      <Stack minHeight={0}>
        <Stack direction="row" justifyContent="space-between">
          <TabsStyle onChange={handleChange} value={tab} variant="scrollable">
            <TabStyle
              value={AddNewClassTab.General}
              label={t("addNewClass.general")}
            />
            {isEditClass && (
              <TabStyle
                value={AddNewClassTab.Advanced}
                label={t("addNewClass.advanced")}
              />
            )}
            {showAttendanceTab && (
              <TabStyle
                value={AddNewClassTab.Attendance}
                label={t("addNewClass.attendance")}
              />
            )}
          </TabsStyle>
        </Stack>
        {tab === AddNewClassTab.Advanced && (
          <Advance
            classId={state.id}
            dispatchState={dispatchState}
            onDirtyChange={setIsAdvancedDirty}
          />
        )}
        {tab === AddNewClassTab.Attendance && showAttendanceTab && (
          <ClassAttendanceForm
            key={state.id}
            value={state.attendance_bonus_amount ?? null}
            onChange={(value) =>
              setState((prev) => ({ ...prev, attendance_bonus_amount: value }))
            }
            onValidChange={(isValid) => {
              setAttendanceFormValid(isValid)
              if (isValid) {
                setShowAttendanceErrors(false)
              }
            }}
            showErrors={showAttendanceErrors}
            schoolDefaultAmount={
              attendanceSettings?.data.attendance_bonus_amount
            }
          />
        )}
        {tab === AddNewClassTab.General && (
          <>
            <Grid container direction="column">
              <Grid mt={3} mb={3}>
                <TextField
                  inputProps={{
                    maxLength: 100,
                  }}
                  error={!!methods.formState.errors.name?.message}
                  {...methods.register("name", {
                    onChange: (e) =>
                      setState((prev) => ({ ...prev, name: e.target.value })),
                  })}
                  label={t("addNewClass.className")}
                  value={state.name}
                />
                {methods.formState.errors.name?.message && (
                  <ErrorStyle>
                    {t("validations.enterMinChars_one", {
                      count: 3,
                    })}
                  </ErrorStyle>
                )}
              </Grid>
              <Grid>
                <TextField
                  error={!!methods.formState.errors.grade?.message}
                  {...methods.register("grade", {
                    onChange: (e) =>
                      setState((prev) => ({ ...prev, grade: e.target.value })),
                  })}
                  select
                  label={t("addNewClass.grade")}
                  value={state.grade}
                >
                  {grades.map((grade, index) => (
                    <MenuItem key={index} value={grade}>
                      {grade}
                    </MenuItem>
                  ))}
                </TextField>
                {methods.formState.errors.grade?.message && (
                  <ErrorStyle>{t("addNewClass.selectOneOption")}</ErrorStyle>
                )}
              </Grid>
            </Grid>
            <Divider sx={{ width: "100%", mt: 6, mb: 3 }} />
            <Box>
              <Typography variant="body3" color="mockup.neutral40">
                {t("addNewClass.selectClassIcon")}
              </Typography>
              <ToggleButtonGroup
                sx={{
                  mt: 4,
                  display: "flex",
                  flexWrap: "wrap",
                }}
                size="small"
                value={state.icon}
                exclusive
                {...methods.register("icon")}
                onChange={(
                  event: React.MouseEvent<HTMLElement>,
                  newIcon: string | null,
                ) => setState((prev) => ({ ...prev, icon: newIcon }))}
              >
                {Object.keys(icons).map((icon, index) => (
                  <ToggleButtonStyled key={index} value={icon}>
                    <DynamicIcon name={icon} variant="outlined" />
                  </ToggleButtonStyled>
                ))}
              </ToggleButtonGroup>
              {methods.formState.errors.icon?.message && (
                <ErrorStyle>{t("addNewClass.selectOneIcon")}</ErrorStyle>
              )}
            </Box>
            <Divider sx={{ width: "100%", mb: 3, mt: 4 }} />
            {id === "form" && allClasses?.length !== 0 && (
              <Grid container sx={{ pl: 3 }} direction="column">
                <Grid>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isApplySettings}
                        onChange={() => {
                          setIsApplySettings(!isApplySettings)
                        }}
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          lineHeight: "24px",
                          color: theme.palette.mockup.neutral0,
                        }}
                      >
                        {t("addNewClass.applySettings")}
                      </Typography>
                    }
                  />
                </Grid>
                <Grid sx={{ pl: 3 }}>
                  <Typography
                    variant="body1"
                    fontWeight="500"
                    sx={{
                      mt: 1,
                      lineHeight: "24px",
                      color: theme.palette.mockup.grey70,
                    }}
                  >
                    {t(
                      "addNewClass.allBonusesStoreItemsJobsExpensesAndFinesFromTheSelectedClassWillBeCopied",
                    )}
                  </Typography>
                  <TextField
                    error={!!methods.formState.errors.copy_from_id?.message}
                    {...methods.register("copy_from_id", {
                      onChange: (e) => setCopyClassId(Number(e.target.value)),
                    })}
                    sx={{ mt: 3, mb: 1 }}
                    select
                    disabled={!isApplySettings}
                    label={t("addNewClass.selectClass")}
                    value={copyClassId || ""}
                  >
                    {allClasses?.map((singleClass) => (
                      <MenuItem key={singleClass.id} value={singleClass.id}>
                        {singleClass.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  {methods.formState.errors.copy_from_id?.message && (
                    <ErrorStyle>{t("addNewClass.selectOneOption")}</ErrorStyle>
                  )}
                </Grid>
              </Grid>
            )}
            <Grid container sx={{ pl: 3, pt: 1 }} direction="column">
              <Grid>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={state.savings_account_option}
                      onChange={(e) => {
                        if (isEditClass && state.savings_account_option) {
                          handleOpenDialogSavingsOff()
                          return
                        }
                        setState((prev) => ({
                          ...prev,
                          savings_account_option: e.target.checked,
                          ...(!e.target.checked && {
                            interest_option: false,
                            round_interest_to_whole_number: false,
                          }),
                        }))
                      }}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        lineHeight: "24px",
                        color: theme.palette.mockup.neutral0,
                      }}
                    >
                      {t("addNewClass.savingsAccount")}
                    </Typography>
                  }
                />
                <Dialog
                  open={isDialogOpenSavingsOff}
                  onClose={handleCloseDialogSavingsOff}
                  onActionButtonClick={() => {
                    setState((prev) => ({
                      ...prev,
                      savings_account_option: false,
                      interest_option: false,
                      round_interest_to_whole_number: false,
                    }))
                    handleCloseDialogSavingsOff()
                  }}
                >
                  <Typography color="mockup.neutral10">
                    {t(
                      "addNewClass.pleaseEnsureStudentsHaveTransitionedAllMoneyFromTheirSavingsToTheirCheckingAccountBeforeTurningOffSavingsAccounts",
                    )}
                  </Typography>
                </Dialog>
              </Grid>
              <Grid sx={{ pl: 3 }}>
                <Typography
                  variant="body1"
                  fontWeight="500"
                  sx={{
                    mt: 1,
                    mb: 2,
                    lineHeight: "24px",
                    color: theme.palette.mockup.grey70,
                  }}
                >
                  {t(
                    "addNewClass.allowYourStudentsToBudgetAndSeeThePowerOfCompoundInterest",
                  )}
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      {...methods.register("interest_option", {
                        onChange: (e) => {
                          userInfo?.user.school_plan === UserSchoolPlan.FREE
                            ? handleOpenDialogUpgradePlan()
                            : setState((prev) => ({
                                ...prev,
                                interest_option: e.target.checked,
                              }))
                          if (!e.target.checked) {
                            setState((prev) => ({
                              ...prev,
                              round_interest_to_whole_number: false,
                            }))
                          }
                        },
                      })}
                      checked={state.interest_option}
                      disabled={!state.savings_account_option}
                    />
                  }
                  label={
                    <Typography
                      variant="body1"
                      fontWeight="500"
                      sx={{
                        mt: 1,
                        lineHeight: "24px",
                        color: !state.savings_account_option
                          ? theme.palette.text.disabled
                          : theme.palette.mockup.grey70,
                      }}
                    >
                      {t(
                        "addNewClass.allowStudentsToEarnInterestOnTheirSavings",
                      )}
                    </Typography>
                  }
                />
                <Dialog
                  open={isDialogOpenUpgradePlan}
                  onClose={handleCloseDialogUpgradePlan}
                  titleText={t("addNewClass.interestOption")}
                  actionAcceptText={t("coteachers.upgrade")}
                  onActionButtonClick={() => {
                    payIt(null)
                  }}
                  isLoading={isLoadingPayments}
                >
                  <Typography>
                    <Trans
                      i18nKey={t(
                        "addNewClass.descriptionUpgradeTeacherInterest",
                      )}
                      components={{
                        Link: HREFLINK,
                      }}
                    />
                  </Typography>
                </Dialog>
                <TextField
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconOffer
                          style={{
                            opacity:
                              !state.savings_account_option ||
                              !state.interest_option
                                ? 0.38
                                : 1,
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                  error={
                    !!methods.formState.errors.saving_interest_rate?.message
                  }
                  sx={{ mt: 3, mb: 1 }}
                  disabled={
                    !state.savings_account_option || !state.interest_option
                  }
                  label={t("addNewClass.interestRate")}
                  value={state.saving_interest_rate}
                  onChange={(e) =>
                    setState({
                      ...state,
                      saving_interest_rate: e.target.value,
                    })
                  }
                />
                <ErrorStyle>
                  {methods.formState.errors.saving_interest_rate?.message}
                </ErrorStyle>
                <TextField
                  error={
                    !!methods.formState.errors.interest_granting_interval
                      ?.message
                  }
                  {...methods.register("interest_granting_interval", {
                    onChange: (e) =>
                      setState((prev) => ({
                        ...prev,
                        interest_granting_interval: e.target.value,
                      })),
                  })}
                  sx={{ mt: 3, mb: 1 }}
                  select
                  disabled={
                    !state.savings_account_option || !state.interest_option
                  }
                  label={t("addNewClass.interestWillBeSent")}
                  value={state.interest_granting_interval}
                >
                  {payCheck_interval.map((pay) => (
                    <MenuItem key={pay.id} value={pay.value}>
                      {pay.label}
                    </MenuItem>
                  ))}
                </TextField>
                {methods.formState.errors.interest_granting_interval
                  ?.message && (
                  <ErrorStyle>{t("addNewClass.selectOneOption")}</ErrorStyle>
                )}
                <Stack direction="row" alignItems="end">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={state.round_interest_to_whole_number}
                        disabled={
                          !state.savings_account_option ||
                          !state.interest_option
                        }
                        onChange={(e) =>
                          setState((prev) => ({
                            ...prev,
                            round_interest_to_whole_number: e.target.checked,
                          }))
                        }
                      />
                    }
                    label={
                      <Typography
                        variant="body1"
                        fontWeight="500"
                        sx={{
                          mt: 1,
                          lineHeight: "24px",
                          color:
                            !state.savings_account_option ||
                            !state.interest_option
                              ? theme.palette.text.disabled
                              : theme.palette.mockup.grey70,
                        }}
                      >
                        {t("addNewClass.roundInterestToNearestWholeNumber")}
                      </Typography>
                    }
                  />
                  <Tooltip title={t("addNewClass.exWillRoundTo")}>
                    <IconQuestionMark
                      tabIndex={0}
                      style={{
                        opacity:
                          !state.savings_account_option ||
                          !state.interest_option
                            ? 0.38
                            : 1,
                      }}
                    />
                  </Tooltip>
                </Stack>
              </Grid>
            </Grid>
          </>
        )}
      </Stack>
      {isEditClass && (
        <UnsavedChangesDialog
          open={!!pendingTab}
          isSaving={isSavingBeforeSwitch}
          onSave={saveAndSwitchTab}
          onDiscard={discardAndSwitchTab}
        />
      )}
    </Box>
  )
}

export default AddNewClass
